import React, { useEffect } from 'react';
import { Settings, HelpCircle, Sun, Moon, Map, Folder, BookOpen, Maximize, Minimize, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContext';
import { useTour } from '../../contexts/TourContext';
import PatentPendingIcon from '../PatentPendingIcon';
import DataSourceSwitcher from '../DataSourceSwitcher';
import SyncStatusIndicator from '../SyncStatusIndicator';

import { useLearn } from '../../context/LearnContext';

const Header: React.FC = () => {
    const { theme, setTheme, setHelpOpen, activeProject, allData, syncStatus, lastSyncTime, dataSourceMode, isFullScreen, setIsFullScreen } = useAppContext();
    const { startTour } = useTour();
    const { setIsLearnOpen } = useLearn();
    const location = useLocation();
    const navigate = useNavigate();
    const isAdminPage = location.pathname.startsWith('/admin') || location.pathname.startsWith('/platform');

    const toggleTheme = () => {
        setTheme(theme === 'light' ? 'dark' : 'light');
    };

    // Escape to exit full screen
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isFullScreen) {
                setIsFullScreen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullScreen, setIsFullScreen]);

    return (
        <header className={`flex-shrink-0 border-b shadow-sm header ${theme === 'dark'
            ? 'bg-slate-800/40 backdrop-blur-md border-slate-700/50'
            : 'bg-card border-border'
            }`}>
            <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                    {isFullScreen && (
                        <button
                            onClick={() => setIsFullScreen(false)}
                            className="p-2 mr-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-all flex items-center gap-2"
                            title="Exit Full Screen (Esc)"
                        >
                            <X size={20} />
                            <span className="font-bold text-sm">EXIT</span>
                        </button>
                    )}

                    {/* Page title */}
                    {!isAdminPage && <h1 className="text-lg font-semibold text-text-primary">Dashboard</h1>}

                    {/* Active Project Indicator */}
                    {activeProject && activeProject !== 'all' && !isAdminPage && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30 rounded-lg">
                            <Folder size={16} className="text-primary" />
                            <span className="text-sm font-medium text-primary">
                                {allData.projectMetadata?.displayName || activeProject}
                            </span>
                        </div>
                    )}

                    {/* Sync Status Indicator */}
                    {!isAdminPage && (
                        <SyncStatusIndicator
                            status={syncStatus}
                            lastSyncTime={lastSyncTime}
                            sourceOfTruth={dataSourceMode as 'backend' | 'localStorage'}
                        />
                    )}
                </div>

                <div className="flex items-center gap-4">
                    {/* Data Source Switcher */}
                    {!isAdminPage && <DataSourceSwitcher />}

                    <PatentPendingIcon />

                    {/* Full Screen Toggle */}
                    <button
                        onClick={() => setIsFullScreen(!isFullScreen)}
                        className={`
                            flex items-center gap-2 px-4 py-2 rounded-xl transition-all shadow-md border-2 
                            ${isFullScreen
                                ? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
                                : 'border-blue-500/50 bg-blue-500 text-white hover:bg-blue-600'
                            }
                        `}
                        title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                    >
                        {isFullScreen ? (
                            <>
                                <Minimize size={20} />
                                <span className="font-bold">Exit Focus</span>
                            </>
                        ) : (
                            <>
                                <Maximize size={20} />
                                <span className="font-bold">Focus Mode</span>
                            </>
                        )}
                    </button>

                    <button
                        onClick={() => setIsLearnOpen(true)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background/80 transition-all shadow-sm border-2 border-blue-500/50 bg-blue-500/10 animate-pulse-slow"
                        title="Open Magnus Academy"
                    >
                        <BookOpen size={18} className="text-blue-500" />
                        <span className="text-blue-500 font-bold">Academy</span>
                    </button>

                    <button
                        onClick={() => startTour()}
                        className="tour-button flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background/80 transition-all shadow-lg border-2 border-primary/50 bg-primary/10"
                        title="Start Product Tour"
                        style={{
                            boxShadow: '0 0 10px rgba(59, 130, 246, 0.3)'
                        }}
                    >
                        <Map size={18} className="text-primary" />
                        <span className="text-primary font-medium">Tour</span>
                    </button>

                    <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-background/80">
                        {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
                    </button>

                    <button
                        onClick={() => console.log('⚙️ Settings panel click')}
                        className="p-2 rounded-full hover:bg-background/80"
                        title="Settings"
                    >
                        <Settings size={20} />
                    </button>

                    <button
                        onClick={() => setHelpOpen(true)}
                        className="p-2 rounded-full hover:bg-background/80"
                    >
                        <HelpCircle size={20} />
                    </button>
                </div>
            </div>
        </header>
    );
};

export default Header;
